import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Testimonial } from '../types';
import { getRandomTestimonials } from '../services/api';
import { trackEvent } from '../analytics';

const Container = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.grey[50]};
  display: flex;
  flex-direction: column;
  position: relative;
  /* Allow vertical scrolling on small screens while preventing horizontal scroll */
  overflow-x: hidden;
  overflow-y: auto;
`;

// Landing page wrapper
const LandingWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Header kalınlığını artırmak için padding'i 16px → 25px yapıyoruz */
  padding: 32px;
  background-color: transparent;
`;

const AboutLink = styled.button`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[700]};
  text-decoration: none;
  background: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const HeaderLine = styled.div`
  width: 100%;
  /* Header kalınlığını artırmak için yükseklik 3px → 18px (15px artış) */
  height: 3px;
  background-color: ${({ theme }) => theme.colors.grey[200]};
  margin-top: 0px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const BusinessLoginButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.grey[400]};
  border-radius: 20px;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: 12px;
  cursor: pointer;
  margin-top: 5px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[100]};
  }

  .icon {
    font-size: 16px;
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.sm};
  text-align: center;
`;

const Footer = styled.footer`
  padding: 0;
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[500]};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FooterText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const FooterEmail = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
`;

// box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
const LogoContainer = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 60px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  margin-top: -79px;
  margin-bottom: 40px;
  background-color: ${({ theme }) => theme.colors.grey[50]};
  position: relative;
  z-index: 1;
`;

const LogoImage = styled.img`
  width: 90%;
  height: auto;
  object-fit: contain;
`;

const LogoFallback = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 60px;
`;

const Title = styled.h1`
  font-size: 40px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.regular};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  line-height: 1.2;

  .highlight {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[600]};
  margin-top: 5px;
  margin-bottom: 40px;
  max-width: 600px;
  line-height: 1.5;
`;

const SearchContainer = styled.div`
  width: 100%;
  max-width: 800px; //
  margin-bottom: 40px;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  /* Sağ tarafa ekstra padding ekleyerek yazının "Ara" butonunun altına girmesini engelle */
  padding: 16px 72px 16px 20px; // 2.5x bigger (was 16px 20px), extra right padding for send icon (≈45px buton + 10px boşluk)
  font-size: 15px; // 2.5x bigger (was 16px)
  border: 1px solid ${({ theme }) => theme.colors.grey[300]};
  border-radius: 62.5px; // 2.5x bigger (was 25px)
  background-color: white;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all 0.2s ease;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey[500]};
    transition: opacity 0.1s ease;
  }
`;

const SendButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 45px;
  height: 45px;
  background-color: white;
  color: ${({ theme }) => theme.colors.grey[500]};
  border: none;
  border-radius: 50%;
  font-size: 45px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[100]};
    color: ${({ theme }) => theme.colors.grey[600]};
    transform: translateY(-50%) scale(1.05);
  }

  &:disabled {
    opacity: 0.6;
    transform: translateY(-50%);
    cursor: not-allowed;
  }
`;

const ExampleSection = styled.section`
  width: 100%;
  max-width: 800px;
`;

const ExampleTitle = styled.h2`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ExampleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const ExampleCard = styled.button`
  padding: ${({ theme }) => theme.spacing.md};
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.grey[200]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  position: relative;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const ExampleText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.4;
`;

const TrendingIcon = styled.img`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 20px;
  height: 20px;
`;

// How It Works Section Styles
const HowItWorksSection = styled.section`
  width: 100%;
  margin: 20px -${({ theme }) => theme.spacing.lg} 0;
  padding: 15px ${({ theme }) => theme.spacing.lg};
  background-color: #F9F9F9;
`;

const HowItWorksTitle = styled.h2`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  text-align: center;
  margin-bottom: 20px;
  margin-top: 5px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 40px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 35px;
    left: calc(16.66% + 20px);
    right: calc(16.66% + 20px);
    height: 1px;
    background: repeating-linear-gradient(
      to right,
      ${({ theme }) => theme.colors.grey[400]} 0,
      ${({ theme }) => theme.colors.grey[400]} 8px,
      transparent 8px,
      transparent 16px
    );
    z-index: 0;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
    align-items: center;
  }
`;

const TestimonialsSection = styled.section`
  width: 100%;
  margin: 32px 0 24px;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    margin: 24px 0 16px;
    padding: 0 ${({ theme }) => theme.spacing.md};
  }
`;

const TestimonialsGrid = styled.div`
  width: 100%;
  max-width: 1000px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 460px));
  justify-content: center;
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }
`;

const TestimonialCard = styled.article`
  width: 100%;
  max-width: 460px;
  justify-self: center;
  background-color: #ffffff;
  border-radius: 32px;
  border: 0.5px solid ${({ theme }) => theme.colors.primary};
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.06), 0 10px 10px rgba(0, 0, 0, 0.04);
  padding: 28px 32px 32px;
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: 768px) {
    padding: 18px 18px 20px;
    border-radius: 24px;
  }

  @media (max-width: 480px) {
    padding: 16px 16px 18px;
  }
`;

const TestimonialHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
`;

const PersonInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AvatarCircle = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }

  @media (max-width: 480px) {
    width: 64px;
    height: 64px;
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PersonText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

// Use a span instead of h3 so we don't inherit any global heading styles
const PersonName = styled.span`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 4px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const PersonLocation = styled.span`
  font-size: 13px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.secondary};

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const ProductImageWrapper = styled.div`
  width: 150px;
  height: 110px;
  border-radius: 24px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);

  @media (max-width: 768px) {
    width: 120px;
    height: 90px;
  }

  @media (max-width: 480px) {
    width: 100px;
    height: 72px;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const QuoteRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const QuoteIcon = styled.img`
  width: 68px;
  height: 68px;
  margin-top: -20px; /* 20px higher than previous position (4px → -16px) */

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    margin-top: -12px;
  }
`;

const QuoteText = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 1.4;
  }
`;

// Skeleton / ghost testimonial styles
const SkeletonTestimonialCard = styled(TestimonialCard)`
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
`;

const SkeletonAvatar = styled(AvatarCircle)`
  background-color: ${({ theme }) => theme.colors.grey[200]};
`;

const SkeletonProductImage = styled(ProductImageWrapper)`
  background-color: ${({ theme }) => theme.colors.grey[200]};
`;

const SkeletonLine = styled.div<{ width?: string }>`
  height: 12px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.grey[200]};
  width: ${({ width }) => width || '100%'};
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StepCard = styled.div`
  flex: 1;
  position: relative;
  padding: 10px 8px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  z-index: 1;
`;

const StepIconContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  /* Beyaz arka plan üzerine gradient ekle - dash'lerin arkasında kalmaması için */
  background: 
    linear-gradient(135deg, ${({ theme }) => theme.colors.primary}20, ${({ theme }) => theme.colors.primary}10),
    white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10; /* Dash çizgilerinin üstünde kalması için z-index artırıldı */
`;

const StepTitle = styled.h3`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  text-align: center;
`;

const StepDescription = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[600]};
  line-height: 1.4;
  margin: 0;
  text-align: center;
  max-width: 200px;
`;

const WelcomeScreen: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoadingTestimonials, setIsLoadingTestimonials] = useState(true);
  const [pendingImageCount, setPendingImageCount] = useState(0);

  const placeholderExamples = [
    "Prize takılan gece lambası...",
    "Fayans arasına sürülen şey...", 
    "Yekpare çerçeveli günlük spor gözlük...",
    "Duvara asılan örgü saksı tutucu...",
    "Taş fırın lezzeti veren fırın tepsisi...",
  ];

  const handleSearch = () => {
    const trimmed = searchText.trim();
    if (trimmed) {
      trackEvent('search_from_home', {
        search_term: trimmed,
        page_path: '/',
      });
      navigate('/chat', { state: { initialMessage: trimmed } });
    }
  };

  const handleExampleClick = (exampleText: string) => {
    trackEvent('example_search_click', {
      example_text: exampleText,
      page_path: '/',
    });
    setSearchText(exampleText);
    navigate('/chat', { state: { initialMessage: exampleText } });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Placeholder animation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPlaceholderIndex((prevIndex) => 
        (prevIndex + 1) % placeholderExamples.length
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [placeholderExamples.length]);

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        setIsLoadingTestimonials(true);
        const data = await getRandomTestimonials(2);

        if (data && data.length > 0) {
          setTestimonials(data);
          const totalImages = data.reduce(
            (acc, t) =>
              acc +
              (t.avatarUrl ? 1 : 0) +
              (t.productImageUrl ? 1 : 0),
            0
          );
          setPendingImageCount(totalImages);
          if (totalImages === 0) {
            setIsLoadingTestimonials(false);
          }
          return;
        }

        // No testimonials returned from API – stop loading and render nothing.
        setTestimonials([]);
        setPendingImageCount(0);
        setIsLoadingTestimonials(false);
      } catch (error) {
        console.error('Failed to load testimonials from API.', error);
        setTestimonials([]);
        setPendingImageCount(0);
        setIsLoadingTestimonials(false);
      }
    };

    loadTestimonials();
  }, []);

  // When all images have either loaded or failed, hide the skeleton
  useEffect(() => {
    if (!isLoadingTestimonials) return;
    if (testimonials.length === 0) return;
    if (pendingImageCount !== 0) return;

    const timeoutId = window.setTimeout(() => {
      setIsLoadingTestimonials(false);
    }, 350); // Keep skeleton at least a short time to avoid flicker

    return () => window.clearTimeout(timeoutId);
  }, [pendingImageCount, testimonials.length, isLoadingTestimonials]);

  const handlePreloadImageDone = () => {
    setPendingImageCount((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const examples = [
    "çekilebilen, ayakları koltuğun altına giren sehpa",
    "fayans arasına sürülen dolgu",
    "saçımı tepede topuz yapan o sünger halka"
  ];

  return (
    <Container>
      {/* Landing Page */}
      <LandingWrapper>
        <Header>
          <AboutLink onClick={() => navigate('/hakkimizda')}>
            Hakkımızda
          </AboutLink>
        </Header>
        
        <HeaderLine />

        <MainContent>
          <LogoContainer>
            <LogoImage 
              src="/sezgi_logo-preview.png" 
              alt="Sezgi Logo"
            />
            <LogoFallback style={{ display: 'none' }}>
              PLACEHOLDER IMAGE
            </LogoFallback>
          </LogoContainer>

          <Title>
            Aklındaki <span className="highlight">o şeyi</span> tarif et.
          </Title>

          <Subtitle>
            Adı dilinin ucunda, Sezgi ile bir tık uzağında.
          </Subtitle>

          <SearchContainer>
            <SearchInput
              type="text"
              placeholder={placeholderExamples[currentPlaceholderIndex]}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <SendButton 
              onClick={handleSearch} 
              disabled={!searchText.trim()}
            >
              <img src="/search_logo.png" alt="Search" style={{width: "50%", height: "50%", opacity: 0.65}} />
            </SendButton>
          </SearchContainer>

          <ExampleSection>
            <ExampleGrid>
              {examples.map((example, index) => (
                <ExampleCard 
                  key={index} 
                  onClick={() => handleExampleClick(example)}
                >
                  <TrendingIcon src="/trending_up.svg" alt="Trending" />
                  <ExampleText>{example}</ExampleText>
                </ExampleCard>
              ))}
            </ExampleGrid>
          </ExampleSection>

          <HowItWorksSection>
            <StepsContainer>
              <StepCard>
                <StepIconContainer>
                  💭
                </StepIconContainer>
                <StepTitle>Tarif Et</StepTitle>
                <StepDescription>
                  Aklındaki 'o şey'i, sanki bir arkadaşına anlatır gibi, birkaç kelimeyle yaz.
                </StepDescription>
              </StepCard>

              <StepCard>
                <StepIconContainer>
                  🎯
                </StepIconContainer>
                <StepTitle>Seç</StepTitle>
                <StepDescription>
                  Sezgi'nin senin için bulduğu ve resmettiği kavramlardan doğru olanı tek bir tıkla seç.
                </StepDescription>
              </StepCard>

              <StepCard>
                <StepIconContainer>
                  ✨
                </StepIconContainer>
                <StepTitle>Keşfet</StepTitle>
                <StepDescription>
                  Artık adını bildiğin o ürünün en iyi alternatiflerini anında karşına getirelim.
                </StepDescription>
              </StepCard>
            </StepsContainer>
          </HowItWorksSection>
        </MainContent>
        <Footer>
          <FooterText>
            Bize <FooterEmail>iletisim@sezgi.co</FooterEmail> adresinden ulaşabilirsiniz.
          </FooterText>
          <div>© 2025 Sezgi</div>
        </Footer>
      </LandingWrapper>
    </Container>
  );
};

export default WelcomeScreen;